import { Children, type HTMLProps, isValidElement, type ReactNode } from 'react'
import { css, type RuleSet, styled } from 'styled-components'
import { tk, withStyleModifiers } from '../../utils'
import { Icon } from '../Icon'
import { Typography } from '../Typography'
import { wrapTextChildrenWithTypography } from '../Typography/wrap-text-children-with-typography'

export type ButtonSize = 'default' | 'small'

export type ButtonHierarchy = 'primary' | 'secondary' | 'tertiary'

interface ButtonProps extends HTMLProps<HTMLButtonElement> {
  $hierarchy?: ButtonHierarchy
  $size?: ButtonSize
  $withoutColor?: boolean
  $fullSize?: boolean
}

function isChildOfType<T,>(type: T) {
  return (child: ReactNode) =>
    isValidElement(child) && child.type === type
}

function hasChildrenOfType<T,>(children: ReactNode | ReactNode[], type: T) {
  return Boolean(Children.toArray(children).find(isChildOfType(type)))
}

function childrenHasOnlyIcon(children: ReactNode | ReactNode[]) {
  return hasChildrenOfType(children, Icon) && Children.count(children) === 1
}

const Button = styled.button.attrs<ButtonProps>(({ children }) => ({
  children: wrapTextChildrenWithTypography(children),
}))`
  border: none;
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: ${tk('radius_rounded')};
  ${Typography} {
    line-height: ${tk('font_line_height_md')};
  }

  ${({ $withoutColor }) => modWithoutColor($withoutColor)}
  ${({ $size }) => modSize($size)}
  ${({ $hierarchy }) => modHierarchy($hierarchy)}
  ${({ $fullSize }) => $fullSize && modFullSize()}
  ${({ children }) => childrenHasOnlyIcon(children) && modIconOnly()}
`

Button.displayName = 'Button'

function modWithoutColor(withoutColor = false): RuleSet<object> {
  return withoutColor
    ? css`
        --button-primary-background-color: ${tk('color_semantic_bg_inverse')};
        --button-primary-background-color-hover: ${tk('color_semantic_bg_inverse_hover')};
        --button-primary-background-color-disabled: ${tk('color_semantic_bg_inverse_disabled')};
        --button-primary-text-color-disabled: ${tk('color_semantic_text_disabled')};

        --button-border-color: ${tk('color_semantic_border')};
        --button-border-color-hover: ${tk('color_semantic_border_hover')};

        --button-secondary-text-color-disabled: ${tk('color_semantic_text_disabled')};

        --button-tertiary-text-color: ${tk('color_semantic_text')};
        --button-tertiary-text-color-disabled: ${tk('color_semantic_text_disabled')};

        --button-focus-shadow: ${tk('shadow_focus_grey')};
      `
    : css`
        --button-primary-background-color: ${tk('color_semantic_bg_primary')};
        --button-primary-background-color-hover: ${tk('color_semantic_bg_primary_hover')};
        --button-primary-background-color-disabled: ${tk('color_semantic_bg_primary_disabled')};
        --button-primary-text-color-disabled: ${tk('color_semantic_text_on_color')};

        --button-border-color: ${tk('color_semantic_border_primary')};
        --button-border-color-hover: ${tk('color_semantic_border_hover')};
        --button-border-color-disabled: ${tk('color_semantic_border_primary_disabled')};

        --button-secondary-border-color-hover: ${tk('color_semantic_border_primary_hover')};
        --button-secondary-text-color: ${tk('color_semantic_text_primary')};
        --button-secondary-text-color-hover: ${tk('color_semantic_text_primary_hover')};
        --button-secondary-text-color-disabled: ${tk('color_semantic_text_primary_disabled')};

        --button-tertiary-text-color: ${tk('color_semantic_text_primary')};
        --button-tertiary-text-color-hover: ${tk('color_semantic_text_primary_hover')};
        --button-tertiary-text-color-disabled: ${tk('color_semantic_text_primary_disabled')};

        --button-focus-shadow: ${tk('shadow_focus_red')};
      `
}

function modSize(sizeName: ButtonSize = 'default'): RuleSet<object> {
  return ({
    small: css`
      padding: ${tk('spacing_xs')};
      min-height: 32px;
      min-width: 32px;

      ${Icon} {
        ${Icon.modSize('sm')}
      }

      ${Typography} {
        ${Typography.modVariant('body-sm-bold')}
      }

      > * + * {
        margin-left: ${tk('spacing_2xs')};
      }
    `,
    default: css`
      padding: ${tk('spacing_sm')} ${tk('spacing_lg')};
      min-height: 48px;
      min-width: 48px;

      ${Icon} {
        ${Icon.modSize('md')}
      }

      ${Typography} {
        ${Typography.modVariant('label')}
        line-height: ${tk('font_line_height_md')};
      }

      > * + * {
        margin-left: ${tk('spacing_xs')};
      }
    `,
  })[sizeName]
}

function modHierarchy(hierarchyName: ButtonHierarchy = 'primary'): RuleSet<object> {
  return ({
    primary: css`
      background: var(--button-primary-background-color);
      color: ${tk('color_semantic_text_on_color')};

      &:hover&:not(:disabled) {
        background: var(--button-primary-background-color-hover);
      }

      &:focus {
        box-shadow: var(--button-focus-shadow);
        background: var(--button-primary-background-color);
      }

      &:disabled {
        background: var(--button-primary-background-color-disabled);
        color: var(--button-primary-text-color-disabled);
      }
    `,
    secondary: css`
      background: ${tk('color_semantic_bg')};

      border: 2px solid;
      border-color: var(--button-border-color);
      color: var(--button-secondary-text-color);

      &:hover&:not(:disabled) {
        border-color: var(--button-secondary-border-color-hover);
        color: var(--button-secondary-text-color-hover);
      }

      &:focus {
        box-shadow: var(--button-focus-shadow);
        border-color: var(--button-border-color);
      }

      &:disabled {
        border-color: var(--button-border-color-disabled);
        color: var(--button-secondary-text-color-disabled);
      }
    `,
    tertiary: css`
      // TODO: This hierarchy has a padding that is inconsistent with Figma design.

      color: var(--button-tertiary-text-color);
      background: none;

      &:hover&:not(:disabled) {
        color: var(--button-tertiary-text-color-hover);
      }

      &:disabled {
        color: var(--button-tertiary-text-color-disabled);
      }
    `,
  })[hierarchyName]
}

function modIconOnly(): RuleSet<object> {
  return css`
  padding: ${tk('spacing_xs')} !important;

  /** Make the size collapse, so it occupies the minimum allowed by the size modifier **/
  width: 0 !important;
  height: 0 !important;
`
}

function modFullSize(): RuleSet<object> {
  return css`
  width: 100%;
`
}

/**
 * @deprecated Use `Button` from `@sunwebgroup/ui-components/uds` instead. Note: Only primary and secondary button variants are supported in the UDS by now, ask designers for any doubt on specific use case/design.
 */
export default withStyleModifiers(Button, {
  modSize,
})
